import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';

const OnboardingHighlight = ({ targetSelector, isActive, padding = 8, borderRadius = 8, children }) => {
  const [targetBounds, setTargetBounds] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const updateBounds = useCallback(() => {
    if (targetSelector && isActive) {
      try {
        const element = document.querySelector(targetSelector);
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

          setTargetBounds({
            top: rect.top + scrollTop - padding,
            left: rect.left + scrollLeft - padding,
            width: rect.width + (padding * 2),
            height: rect.height + (padding * 2),
          });

          // Only scroll if element is not already mostly visible
          const windowHeight = window.innerHeight;
          const elementTop = rect.top;
          const elementBottom = rect.bottom;
          const isMobile = window.innerWidth <= 768;

          // For mobile, we need the element visible in the upper portion of the screen
          // so it doesn't get hidden behind the onboarding card at the bottom
          if (isMobile) {
            // The onboarding card takes roughly 220px at the bottom
            // We want the element to be visible above it
            const cardHeight = 220;
            const safeViewportHeight = windowHeight - cardHeight - 20; // 20px buffer

            // Check if element is visible in the safe area
            const isVisible = elementTop > 20 && elementBottom < safeViewportHeight;

            if (!isVisible) {
              setTimeout(() => {
                // Scroll to position element in the middle of the safe area
                const targetPosition = element.getBoundingClientRect().top + window.scrollY - (safeViewportHeight / 2) + (rect.height / 2);
                window.scrollTo({
                  top: targetPosition,
                  behavior: 'smooth'
                });
              }, 300);
            }
          } else {
            // Desktop behavior
            const isVisible = elementTop > windowHeight * 0.1 && elementBottom < windowHeight * 0.9;

            if (!isVisible) {
              setTimeout(() => {
                element.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'nearest'
                });
              }, 200);
            }
          }
        } else {
          setTargetBounds(null);
        }
      } catch (error) {
        console.warn('Onboarding: Error updating bounds', error);
        setTargetBounds(null);
      }
    } else if (!targetSelector && isActive) {
      setTargetBounds(null);
    }
  }, [targetSelector, isActive, padding]);

  useEffect(() => {
    // Initial update
    updateBounds();

    // Only update on significant events, not continuously
    const handleResize = () => {
      requestAnimationFrame(updateBounds);
    };

    const handleScroll = () => {
      requestAnimationFrame(updateBounds);
    };

    if (isActive) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [updateBounds, isActive]);

  if (!isActive) return null;

  const overlayContent = (
    <AnimatePresence>
      {isActive && (
        <>
          <motion.div
            ref={overlayRef}
            className="fixed inset-0 z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.5,
              ease: [0.25, 0.1, 0.25, 1]
            }}
            style={{ pointerEvents: 'none' }}
          >
            {targetBounds ? (
              <svg
                className="absolute inset-0 w-full h-full"
                style={{ pointerEvents: 'none' }}
              >
                <defs>
                  <mask id="spotlight-mask">
                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                    <motion.rect
                      initial={false}
                      animate={{
                        x: targetBounds.left,
                        y: targetBounds.top - window.pageYOffset,
                        width: targetBounds.width,
                        height: targetBounds.height
                      }}
                      transition={{
                        duration: 0.6,
                        ease: [0.25, 0.1, 0.25, 1]
                      }}
                      rx={borderRadius}
                      fill="black"
                    />
                  </mask>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <rect
                  x="0"
                  y="0"
                  width="100%"
                  height="100%"
                  mask="url(#spotlight-mask)"
                  className="fill-black/90"
                />
                <motion.rect
                  initial={false}
                  animate={{
                    x: targetBounds.left - 2,
                    y: targetBounds.top - window.pageYOffset - 2,
                    width: targetBounds.width + 4,
                    height: targetBounds.height + 4
                  }}
                  transition={{
                    duration: 0.6,
                    ease: [0.25, 0.1, 0.25, 1]
                  }}
                  rx={borderRadius}
                  fill="none"
                  stroke="url(#borderGradient)"
                  strokeWidth="2"
                  filter="url(#glow)"
                />
                <defs>
                  <linearGradient id="borderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="#10b981" stopOpacity="0.6" />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.8" />
                  </linearGradient>
                </defs>
              </svg>
            ) : (
              <div className="absolute inset-0 bg-black/90" />
            )}
          </motion.div>

          <motion.div
            className="fixed z-[9999]"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{
              duration: 0.4,
              ease: [0.25, 0.1, 0.25, 1]
            }}
            style={{ pointerEvents: 'auto' }}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(overlayContent, document.body);
};

export default OnboardingHighlight;